import { useState, useRef, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Send, Bot, User, Loader2, Sparkles,
  MessageCircle, Lightbulb, HelpCircle
} from 'lucide-react';
import { useLanguage } from '../i18n';

export const ChatPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const suggestions = [
    { icon: HelpCircle, text: t('chatPage.suggestions.rosca') },
    { icon: Lightbulb, text: t('chatPage.suggestions.amount') },
    { icon: MessageCircle, text: t('chatPage.suggestions.rules') },
  ];

  useEffect(() => {
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: t('chatPage.welcomeMessage', { firstName: user?.full_name?.split(' ')[0] || '' })
    }]);
  }, [user, t]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.sendMessage(text, sessionId);
      const { response: aiResponse, session_id } = response.data;
      
      if (!sessionId) {
        setSessionId(session_id);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('chatPage.errorMessage'),
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSuggestionClick = (text) => {
    sendMessage(text);
  };

  return (
    <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="chat-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg">
          <Bot className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50">
            {t('chatPage.title')}
          </h1>
          <p className="text-sm text-stone-500 flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            {t('chatPage.poweredByAi')}
          </p>
        </div>
      </div>

      {/* Chat Area */}
      <Card className="card-neo flex-1 flex flex-col overflow-hidden">
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${index}`}
              >
                {message.role === 'assistant' && (
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30">
                      <Bot className="w-4 h-4 text-orange-600" />
                    </AvatarFallback>
                  </Avatar>
                )}
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-stone-900 dark:bg-stone-50 text-white dark:text-stone-900'
                      : message.error
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
                      : 'bg-stone-100 dark:bg-stone-800 text-stone-900 dark:text-stone-50'
                  }`}
                >
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                    {message.content}
                  </p>
                </div>
                {message.role === 'user' && (
                  <Avatar className="w-8 h-8 flex-shrink-0">
                    <AvatarFallback className="bg-stone-200 dark:bg-stone-700">
                      <User className="w-4 h-4 text-stone-600 dark:text-stone-300" />
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3">
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-orange-100 dark:bg-orange-900/30">
                    <Bot className="w-4 h-4 text-orange-600" />
                  </AvatarFallback>
                </Avatar>
                <div className="bg-stone-100 dark:bg-stone-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-orange-600" />
                    <span className="text-sm text-stone-500">{t('chatPage.thinking')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Suggestions */}
        {messages.length <= 1 && (
          <div className="p-4 border-t border-stone-100 dark:border-stone-800">
            <p className="text-xs text-stone-500 mb-3 uppercase tracking-wider">{t('chatPage.suggestionsTitle')}</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, index) => (
                <Button
                  key={index}
                  variant="outline"
                  size="sm"
                  className="gap-2 text-sm"
                  onClick={() => handleSuggestionClick(suggestion.text)}
                  data-testid={`suggestion-${index}`}
                >
                  <suggestion.icon className="w-3 h-3" />
                  {suggestion.text}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <form 
          onSubmit={handleSubmit} 
          className="p-4 border-t border-stone-100 dark:border-stone-800"
        >
          <div className="flex gap-3">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chatPage.inputPlaceholder')}
              className="flex-1 h-12"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button 
              type="submit" 
              className="btn-accent h-12 px-6"
              disabled={loading || !input.trim()}
              data-testid="chat-send-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ChatPage;
